import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Inventory as InventoryIcon,
  AccountTree as WorkflowIcon,
  SortByAlpha as FifoIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';

// ── Sub-components (mirrors LotsHelpModal / InventoryHelpModal pattern) ─────────

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex', fontSize: 18 }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
  accent?: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children, accent }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 150,
          flexShrink: 0,
          color: accent ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

const InventoryModuleHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark
            ? 'rgba(22, 22, 35, 0.90)'
            : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          boxShadow: isDark
            ? '0 32px 80px rgba(0,0,0,0.6)'
            : '0 32px 80px rgba(0,0,0,0.16)',
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Inventory — How It Works
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Core concepts, workflows, and common mistakes
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3.5 }}>

        {/* Intro */}
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Nota inventory tracks every stock change as an immutable movement record — a received delivery,
          a transfer, a waste write-off, a stock count variance, or a production run. Movements are never
          deleted. If you make a mistake, you post a correcting movement. This makes the ledger a
          complete, tamper-evident audit trail.
        </Typography>

        <Divider />

        {/* ── Core Concepts ──────────────────────────────────────────────────── */}
        <Section icon={<InventoryIcon fontSize="inherit" />} title="Core Concepts">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Movement" accent>
              The source of truth. Every quantity change creates a new movement record.
              Movements are never edited or deleted.
            </FieldRow>
            <FieldRow label="Balance">
              A cached running total maintained by the system. If all balances were deleted,
              they could be rebuilt by replaying the movement ledger.
            </FieldRow>
            <FieldRow label="Item Types" accent>
              <strong>WINE</strong> — wine-catalogue integrated. <strong>PURCHASED</strong> — ordered
              from suppliers. <strong>MANUFACTURED</strong> — produced in-house; not eligible for reorder.
            </FieldRow>
            <FieldRow label="Category">
              A venue-defined classification label (e.g. Wine, Beer, Spirits, Food) with optional
              subcategories, managed under Inventory → Categories. Used for filtering and reporting;
              does not change system behaviour.
            </FieldRow>
            <FieldRow label="Location">
              Stock is tracked per physical location. The same wine in the cellar and at the bar
              has two separate balance buckets.
            </FieldRow>
            <FieldRow label="Lot">
              An optional batch sub-bucket within a location. When lot tracking is on, every movement
              links to a specific lot. Items without lot tracking use a single null-lot bucket.
            </FieldRow>
            <FieldRow label="Ownership Account">
              Tracks who owns the stock — venue-owned or consigned by a supplier. A reporting
              dimension only; it does not block operations.
            </FieldRow>
          </Box>
          <Note>
            <strong>Negative stock is allowed.</strong> The system warns but does not block
            going negative. Real cellars have counting errors. Reconcile via a stock count or
            a correcting adjustment.
          </Note>
        </Section>

        <Divider />

        {/* ── Common Workflows ────────────────────────────────────────────────── */}
        <Section icon={<WorkflowIcon fontSize="inherit" />} title="Common Workflows">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
            <FieldRow label="Receive a delivery" accent>
              Find the PO → Receive → enter quantities per line → set destination location.
              Each line creates one RECEIVE movement.
            </FieldRow>
            <FieldRow label="Restock the bar" accent>
              Inventory → Transfers → New Transfer. Select item, From: Cellar, To: Bar.
              Leave lot blank — FIFO allocates from oldest stock automatically.
            </FieldRow>
            <FieldRow label="Monthly wine count" accent>
              Stock Counts → New Count → load with Category: Wine. Count each bin.
              Review variances, then Post. Variances create STOCK_COUNT_VARIANCE movements.
            </FieldRow>
            <FieldRow label="Write off damaged stock" accent>
              Inventory → Adjustments → Direction: Out, Type: WASTE.
              Leave lot blank for FIFO, or select a specific lot for targeted write-off.
            </FieldRow>
            <FieldRow label="Reorder low stock" accent>
              Inventory → Alerts → filter as needed → Enable Selection Mode → check items
              → Create Purchase Order. MANUFACTURED items are excluded — produce them instead.
            </FieldRow>
            <FieldRow label="Run a production batch" accent>
              Inventory → Production Orders → New Order → pick recipe → set batch count.
              Ingredients are consumed via PRODUCTION_INPUT movements; output is posted
              via PRODUCTION_OUTPUT.
            </FieldRow>
          </Box>
          <Note variant="warning">
            There is no edit or delete for movements. To correct a mistake: post a new
            movement in the opposite direction with a note explaining the original error.
          </Note>
        </Section>

        <Divider />

        {/* ── Auto FIFO ───────────────────────────────────────────────────────── */}
        <Section icon={<FifoIcon fontSize="inherit" />} title="Auto FIFO — Why Stock Movements Can Split Across Lots">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            When you post an outbound movement against a lot-tracked item without selecting a specific
            lot, the system automatically allocates from the <strong>oldest lots first</strong> (FIFO —
            first in, first out). If the quantity spans more than one lot, the system creates
            <strong> one movement per lot consumed</strong>.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Example" accent>
              Transfer 5 bottles; Lot A (Jan) has 2, Lot B (Feb) has 4. Result: two movements —
              OUT 2 from Lot A, OUT 3 from Lot B. Both appear in the movement feed.
            </FieldRow>
            <FieldRow label="When to override">
              Select a specific lot manually when you need to consume a known batch — recalled
              stock, damaged-lot write-off, or expiry handling.
            </FieldRow>
            <FieldRow label="For routine use">
              Leave the lot field blank for transfers, production inputs, and daily waste.
              FIFO ensures oldest stock exits first without manual selection every time.
            </FieldRow>
          </Box>
          <Note>
            <strong>FEFO</strong> (first-expired, first-out) is the expiry-date variant of FIFO.
            Set on items where expiry management is critical (food, produce). The system orders
            lots by nearest expiry date first instead of received date.
          </Note>
        </Section>

        <Divider />

        {/* ── Common Mistakes ─────────────────────────────────────────────────── */}
        <Section icon={<WarningIcon fontSize="inherit" />} title="Common Mistakes to Avoid">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <FieldRow label="Wrong location" accent>
              Always check the location field before submitting. Fix by posting OUT from wrong
              location and IN to correct location — both with a note referencing the mistake.
            </FieldRow>
            <FieldRow label="Adding stock to fix negatives" accent>
              If stock is negative because a delivery wasn't received, receive the delivery.
              A generic ADJUSTMENT IN without a paper trail makes audits impossible.
            </FieldRow>
            <FieldRow label="Skipping lot fields on expiry items" accent>
              For FEFO items, omitting the expiry date at receiving means the lot won't be
              deprioritised. Always enter best-before dates at receiving time.
            </FieldRow>
            <FieldRow label="Lot override for waste (outbound)" accent>
              The "Post to unassigned stock" override is <strong>inbound only</strong>. For
              outbound waste on lot-tracked items, leave lot blank (FIFO) or select a named lot.
            </FieldRow>
            <FieldRow label="Cancelling a received PO" accent>
              Cancelling a PO does not reverse receiving movements. Received stock remains
              in inventory. Use a manual adjustment to reverse a receive if needed.
            </FieldRow>
          </Box>
        </Section>

        <Box sx={{ height: 8 }} />

      </DialogContent>

      {/* ── Sticky footer ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ flex: 1, alignSelf: 'center', fontSize: '0.72rem' }}
        >
          Full documentation is available in the inventory operator manual.
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
};

export default InventoryModuleHelpModal;
